import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import hero from "@/assets/trailer-livestock-black.jpg";
import heroPicture from "@/assets/trailer-livestock-black.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import shareImage from "@/assets/trailer-livestock-black.jpg";

export const Route = createFileRoute("/trailer-wiring")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Trailer Wiring Replacement in Seguin, TX | Stock & Livestock Trailer Lights & Plugs | Texas Made Shade",
      description: "Basic trailer wiring replacement in Seguin, TX — 7-way plugs, light repair, and simple rewiring for stock trailers, livestock haulers.",
      image: shareImage,
      socialTitle: "Trailer Wiring Replacement — Texas Made Shade",
      socialDescription: "Simple trailer wiring fixes and plug replacements for livestock and stock trailers in Seguin, TX.",
      keywords: "trailer wiring replacement Seguin TX, trailer light repair Texas, 7 way plug replacement, stock trailer wiring, livestock trailer lights, trailer wiring fix, stock trailer covers, cattle trailer covers",
    }),
    scripts: serviceHeadScripts({
      path: "/trailer-wiring",
      serviceName: "Trailer Wiring Replacement",
      breadcrumbName: "Trailer Wiring",
      description: "Basic trailer wiring replacement and light repair for stock and livestock trailers in Seguin, TX.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/trailer-wiring"
      serviceName="Trailer Wiring"
      eyebrow="TRAILER WIRING"
      title="Basic Trailer Wiring Replacement in Seguin, TX"
      intro="If your stock trailer lights are out, your 7-way plug is corroded, or you just need the basics rewired, we handle simple trailer wiring replacement right here in Seguin, TX. No full custom rewires — just straightforward fixes to get your lights, brakes, and signals working again before your next haul."
      heroImage={heroPicture}
      heroCta={{
        eyebrow: "Trailer Wiring Fixes · Seguin, TX",
        headline: "Need Your Trailer Lights Fixed? Get a Free Estimate Today.",
        subhead: "Tell us what's not working — lights, plug, brakes, or signals — and we'll come back same business day with a plan and a no-pressure quote.",
        primaryHref: "/estimate-quotes#stock-trailer",
        primaryLabel: "Get My Free Estimate",
        phoneNumber: "+1 (830) 390-9588",
      }}
      bodyLayout="accordion"
      features={[
        "7-way RV plug replacement and repair",
        "Trailer light repair — brake, tail, turn, and marker lights",
        "Basic wire replacement for stock and livestock trailers",
        "Simple ground and connection fixes",
        "Corroded plug and socket replacement",
        "Serving Seguin, San Antonio, Austin, Houston, and Central Texas",
      ]}
      keywordOutline={[
        { id: "overview", label: "Trailer Wiring Replacement Overview" },
        { id: "why-us", label: "Why Texas Made Shade for Trailer Wiring" },
        { id: "details", label: "What We Replace & Repair" },
        { id: "applications", label: "Trailers We Service" },
        { id: "faqs", label: "Trailer Wiring FAQs" },
      ]}
      body={[
        {
          heading: "What We Do",
          text: "We replace basic trailer wiring components — plugs, lights, and connections — to get your trailer road-legal and safe. This is straightforward repair work, not full custom electrical builds. If your lights are dim, your plug is green with corrosion, or a wire got pinched on the road, we fix it and get you hauling again.",
          bullets: [
            "7-way plug and socket replacement",
            "Tail, brake, turn, and marker light repair",
            "Basic wire runs and connection fixes",
            "Ground wire repair and cleaning",
            "Quick turnaround — most jobs same day or next day",
          ],
        },
        {
          heading: "When to Bring It In",
          text: "If you're dealing with any of these common issues, it's time to stop by our Seguin shop for a quick wiring fix.",
          bullets: [
            "One or more lights not working",
            "7-way plug cracked, corroded, or loose",
            "Lights flicker or go out when hitting bumps",
            "Brake controller showing a wiring fault",
            "Recent wire damage from a gate hinge, spring, or axle",
          ],
        },
        {
          heading: "What We Don't Do",
          text: "We stick to basic wiring replacement and repair. We do not perform full custom trailer electrical builds, auxiliary power system installs, or complex multi-circuit rewiring projects. If you need a complete rewire from scratch or custom electrical fabrication, we can refer you to a specialty trailer electrical shop.",
        },
      ]}
      applications={[
        "Livestock Gooseneck Trailers",
        "Bumper-Pull Stock Trailers",
        "Horse Trailers",
        "Equipment & Flatbed Trailers",
        "Utility & Cargo Trailers",
      ]}
      faqs={[
        {
          q: "Do you replace 7-way trailer plugs?",
          a: "Yes. A cracked or corroded 7-way plug is one of the most common fixes we do. We cut off the old plug, test the wires, and install a new molded 7-way RV connector. If the socket on your truck side is damaged, we can replace that too.",
        },
        {
          q: "Can you fix trailer lights that keep going out?",
          a: "Usually yes. Intermittent lights are typically a bad ground, a loose connection, or a damaged wire at a hinge or axle. We trace the issue, repair the connection, and test every light before the trailer leaves the shop.",
        },
        {
          q: "How long does a basic wiring fix take?",
          a: "Most plug replacements and light repairs take 1–2 hours. Simple wire runs or ground fixes are usually same-day. We'll give you a time estimate when you drop it off or call ahead.",
        },
        {
          q: "Do you work on all brands of stock trailers?",
          a: "Yes. We replace wiring on gooseneck and bumper-pull stock trailers from Gooseneck Trailer Mfg., W-W, Calico, Featherlite, GR, Titan, and custom builds. Every trailer is different, but basic plug and light work is universal.",
        },
        {
          q: "Where is your shop and do you service trailers from outside Seguin?",
          a: "Our shop is in Seguin, TX. We service stock trailers from San Antonio, New Braunfels, Austin, Houston, Corpus Christi, and across Central Texas. Drop the trailer off or call +1 (830) 390-9588 to schedule a wiring repair.",
        },
      ]}
      cityCallouts={[
        {
          to: "/seguin",
          city: "Seguin",
          anchorText: "Trailer Wiring Shop in Seguin, TX",
          blurb: "Our home shop. Drop your gooseneck or stock trailer off for plug replacement, light repair, or a quick wiring fix.",
        },
        {
          to: "/san-antonio",
          city: "San Antonio",
          anchorText: "Stock Trailer Wiring for San Antonio & Bexar County",
          blurb: "Basic trailer wiring fixes and 7-way plug replacements for livestock trailers across San Antonio and South Texas.",
        },
        {
          to: "/austin",
          city: "Austin",
          anchorText: "Trailer Light Repair & Plug Replacement for Austin, TX",
          blurb: "Gooseneck and stock trailer wiring services delivered into Austin and the I-35 corridor.",
        },
        {
          to: "/houston",
          city: "Houston",
          anchorText: "Livestock Trailer Wiring for Greater Houston",
          blurb: "Trailer plug and light repair for Houston-area ranchers and haulers.",
        },
        {
          to: "/corpus-christi",
          city: "Corpus Christi",
          anchorText: "Trailer Wiring for the Coastal Bend",
          blurb: "Corrosion-resistant plug and light replacement for stock trailers in salt-air environments.",
        },
      ]}
    />
  ),
});
